import React from "react";
import { Link } from "react-router-dom";

import { Language } from "@utils";

interface Lesson {
  title: string;
  content: React.ReactNode;
  pattern: string;
  savedwork?: string;
  answers: string[];
  target: string;
  followupTitle?: string;
  followup?: React.ReactNode;
  language?: Language;
}

export const COMPOSITION_LESSONS: Lesson[] = [
  {
    title: "Either / Or",
    content: (
      <div>
        <p>
          In the Basics Tutorial, your learned about Semgrep <em>patterns</em>,
          which are the basic building blocks of Semgrep <em>rules</em>.
        </p>
        <p>
          Semgrep <em>rules</em> are written in{" "}
          <a href="https://docs.ansible.com/ansible/latest/reference_appendices/YAMLSyntax.html">
            YAML
          </a>
          , and contain one or more patterns, along with some other metadata
          like a message to display whenever the rule finds a match. This YAML
          syntax enables us to combine patterns with{" "}
          <strong>logic operators</strong>.
        </p>
        <p>
          If you want to match <em>either</em> pattern1 OR pattern2, use{" "}
          <code>pattern-either</code>.
        </p>
        <p>
          In the example provided, two variations of <code>==</code> can be
          matched using <code>pattern-either</code>.
        </p>
        <p>
          <em>
            Complete the second pattern, where the string literal is on the left
            and metavariable is on the right.
          </em>
        </p>
      </div>
    ),
    language: "java",
    pattern: `rules:
  - id: use-string-equals
    message: In Java, do not use == with strings. Use String.equals() instead.
    pattern-either:
      - pattern: if ($X == "...") ...
      - pattern: TODO`,
    answers: [
      `rules:
  - id: use-string-equals
    message: In Java, do not use == with strings. Use String.equals() instead.
    pattern-either:
      - pattern: if ($X == "...") ...
      - pattern: if ("..." == $X) ...`,
    ],
    target: `public class Example {
  public int foo(String a, int b) {
      if (a == "hello") return 1;
      // Match here too by adding another pattern clause.
      if ("hello" == a) return 2;
      // Do not match here
      print("hello")
  }
}
`,
    followupTitle: "Precisely!",
    followup: (
      <>
        <p>
          In fact, you are not limited to two patterns under the{" "}
          <code>pattern-either</code>. Many of the rules on the Rules page use 5
          or more patterns that are all OR'd together!
        </p>
        <p>The sky is the limit!</p>
        <span style={{ fontSize: "2em" }} role="img" aria-label=":rocket:">
          🚀
        </span>
      </>
    ),
  },
  {
    title: "Pattern is not",
    content: (
      <div>
        <p>
          You can use <code>pattern-not</code> to filter out patterns you do not
          want to match.
        </p>
        <p>
          The <code>pattern-not</code> is listed as another item under{" "}
          <code>patterns</code>, since you want to find code that matches the
          first pattern AND does not match the second.
        </p>
        <p>
          <em>
            In the example here, filter out patterns where the first argument of{" "}
            <code>subprocess.call()</code> is a string.
          </em>
        </p>
      </div>
    ),
    pattern: `rules:
  - id: subprocess-call
    patterns:
    - pattern: subprocess.call(...)`,
    answers: [
      `rules:
  - id: subprocess-call
    patterns:
    - pattern: subprocess.call(...)
    # This says never match if first argument is a string
    - pattern-not: subprocess.call("...", ...)`,
      `rules:
  - id: subprocess-call
    patterns:
    - pattern: subprocess.call(...)
    - pattern-not: subprocess.call("...", ...)`,
    ],
    target: `import subprocess

subprocess.call("ls -a .")  # Try not to match here.

dir = "/tmp"
subprocess.call("ls -a " + dir) # or here

subprocess.call(dir, shell=True) # or here!

subprocess.call(nonstring) # MATCH THIS

subprocess.call(nonstring, shell=True) # and this!
`,
    followupTitle: "Keep it up!",
    followup: (
      <>
        <p>
          As with <code>pattern-either</code>, you are not limited to just one{" "}
          <code>pattern-not</code>.
        </p>
        <p>
          In fact, you can even combine them to write code that means "code is
          either this OR that OR that, AND is not this"
        </p>
        <span style={{ fontSize: "2em" }} role="img" aria-label=":rocket:">
          🚀🚀
        </span>
        <p>
          There are many examples of this in the pre-written rules on the Rules
          tab. But first, stay here to learn about <code>pattern-inside</code>{" "}
          and <code>metavariable-regex</code>!
        </p>
      </>
    ),
  },
  {
    title: "Pattern is inside",
    content: (
      <div>
        <p>
          As the name implies, <code>pattern-inside</code> lets you search for
          patterns <strong>inside</strong> the pattern specified by{" "}
          <code>pattern-inside</code>.
        </p>
        <p>
          A few useful scenarios include: searching inside function definitions,
          searching before or after function calls, and verifying that a certain
          module has been imported.
        </p>
        <p>
          Note that the pipe (<code>|</code>) after <code>pattern-inside</code>{" "}
          is YAML syntax that permits multi-line strings.
        </p>
        <p>
          <em>
            Try to match <code>http.ResponseWriter.Write()</code> in the code
            provided. Your starting rule will also match{" "}
            <code>bytes.Buffer.Write()</code>. Use <code>pattern-inside</code>{" "}
            to only search inside functions which have a parameter of type{" "}
            <code>http.ResponseWriter</code> (this challenge is in Go).
          </em>
        </p>
      </div>
    ),
    language: "go",
    pattern: `rules:
  - id: http-responsewriter-write
    patterns:
    - pattern-inside: |
        func $FUNC(...) {
          ...
        }
    - pattern: $WRITER.Write(...)`,
    answers: [
      `rules:
  - id: http-responsewriter-write
    patterns:
    - pattern-inside: |
        func $FUNC(..., $WRITER http.ResponseWriter, ...) {
          ...
        }
    - pattern: $WRITER.Write(...)`,
    ],
    target: `package main

import (
  "bytes"
  "fmt"
  "log"
  "net/http"
)

func writeMessage(msg string, buf *bytes.Buffer) {
  // Try not to match here by using pattern-inside.
  buf.Write([]byte(msg))
}

func errorPage(hw http.ResponseWriter, r *http.Request) {
  hw.WriteHeader(http.StatusAccepted)
  hw.Write([]byte("Error!"))
}

func indexPage(w http.ResponseWriter, r *http.Request) {
  const template = \`
  <html>
  <body>
    <h1>Greetings!</h1>
    <h2>%s</h2>
  </body>
  </html>\`

  w.WriteHeader(http.StatusAccepted)
  w.Write([]byte(fmt.Sprintf(template, "Semgrep!")))
}

func main() {
  http.HandleFunc("/", indexPage)
  http.HandleFunc("/error", errorPage)
  http.ListenAndServe(":8080", nil)
}`,
    followupTitle: "You're Amazing!",
    followup: (
      <>
        <p>
          We have covered <code>pattern-not</code> and{" "}
          <code>pattern-inside</code>
        </p>
        <p>You can probably guess what the next one is</p>
        <p>...</p>
        <span style={{ fontSize: "2em" }} role="img" aria-label=":rocket:">
          👀
        </span>
        <p>...</p>
      </>
    ),
  },
  {
    title: "Pattern is not inside",
    content: (
      <div>
        <p>
          A <code>pattern-not-inside</code> <strong>filters out</strong> any
          matches inside the <strong>range</strong> defined by the pattern.
        </p>

        <p>
          A common use is to filter out matches that are called{" "}
          <strong>after</strong> a certain function. For example, consider
          detecting cookies in Java that <em>do not</em> have the{" "}
          <code>secure</code> flag set.
        </p>
        <p>
          In Java, setting the <code>secure</code> flag is accomplished by
          instantiating a Cookie object, calling <code>setSecure(true)</code> on
          it, and finally adding it to the response.
        </p>
        <p>
          <em>
            Use <code>pattern-not-inside</code> to filter out cases where{" "}
            <code>setSecure(true)</code> has been called. Your{" "}
            <code>pattern-not-inside</code> pattern must use ellipses in order
            to capture everything that happens AFTER{" "}
            <code>setSecure(true)</code> is called. Try using the YAML
            multi-line | symbol and remember your semicolons.
          </em>
        </p>
      </div>
    ),
    language: "java",
    pattern: `rules:
  - id: secure-flag-not-set
    patterns:
    - pattern: $RESPONSE.addCookie($COOKIE);`,
    answers: [
      `rules:
  - id: secure-flag-not-set
    patterns:
    - pattern: $RESPONSE.addCookie($COOKIE);
    - pattern-not-inside: |
        $COOKIE.setSecure(true);
        ...`,
      `rules:
  - id: secure-flag-not-set
    patterns:
    - pattern: $RESPONSE.addCookie($COOKIE);
    - pattern-not-inside: |
        $COOKIE.setSecure(true);
        ...
        $RESPONSE.addCookie($COOKIE);`,
      `rules:
  - id: secure-flag-not-set
    patterns:
    - pattern-not-inside: |
        $COOKIE.setSecure(true);
        ...
        $RESPONSE.addCookie($COOKIE);
    - pattern: $RESPONSE.addCookie($COOKIE);`,
      `rules:
  - id: secure-flag-not-set
    patterns:
    - pattern-not-inside: |
        $COOKIE.setSecure(true);
        ...
    - pattern: $RESPONSE.addCookie($COOKIE);`,
      `rules:
  - id: secure-flag-not-set
    patterns:
    - pattern: $RESPONSE.addCookie($COOKIE);
    - pattern-not-inside: $COOKIE.setSecure(true); ...`,
      `rules:
  - id: secure-flag-not-set
    patterns:
    - pattern-not-inside: $COOKIE.setSecure(true); ...
    - pattern: $RESPONSE.addCookie($COOKIE);`,
    ],
    target: `@Controller
public class CookieController {

    @RequestMapping(value = "/cookie1", method = "GET")
    public void setCookie(@RequestParam String value, HttpServletResponse response) {
        Cookie cookie = new Cookie("cookie", value);
        response.addCookie(cookie);
    }

    @RequestMapping(value = "/cookie2", method = "GET")
    public void setSecureCookie(@RequestParam String value, HttpServletResponse response) {
        Cookie cookie = new Cookie("cookie", value);
        cookie.setSecure(true);
        String mystr = "this line should not interfere";
        response.addCookie(cookie); // Try not to match here.
    }
}
`,
    followupTitle: "Last One!",
    followup: (
      <>
        <p>
          Our final challenge will show you how to specify a{" "}
          <code>metavariable</code> that conforms to a particular{" "}
          <code>regular expression</code>.
        </p>
        <p>
          It is great for when you want to search for strings within your
          strings, or only match a particular set of methods in a class, like
          those that start with the word "test"!
        </p>
        <span style={{ fontSize: "3em" }} role="img" aria-label=":nesting:">
          🍱
        </span>
        <span style={{ fontSize: "2em" }} role="img" aria-label=":nesting:">
          🍱
        </span>
        <span style={{ fontSize: "1em" }} role="img" aria-label=":nesting:">
          🍱
        </span>
        <p>
          Basically, it allows you to write code about your code about your
          code.
        </p>
        <span style={{ fontSize: "3em" }} role="img" aria-label=":brain:">
          🤯
        </span>
      </>
    ),
  },
  {
    title: "Metavariable Regex",
    content: (
      <div>
        <p>
          One final Semgrep pattern type that is very useful is called{" "}
          <code>metavariable-regex</code>.
        </p>
        <p>
          It allows you to specify that certain metavariables{" "}
          <strong>only</strong> match variables whose names fit a specified
          regular expression.
        </p>
        <p>
          The example provided will match calls to{" "}
          <code>django.db.models.FloatField(...)</code> <strong>only</strong> if
          the left-hand side of the equation contains the word "fee" or
          "salary".
        </p>
        <p>
          <em>
            Change the example to also match when the return value contains the
            word "price".
          </em>
        </p>
      </div>
    ),
    pattern: `rules:
  - id: use-decimalfield-for-money
    patterns:
    - pattern-inside: |
        class $M(...):
          ...
    - pattern: $F = django.db.models.FloatField(...)
    - metavariable-regex:
        metavariable: '$F'
        regex: '.*(fee|salary).*'
    message: Found a FloatField used for variable $F. Use DecimalField for currency fields to avoid float-rounding errors.
    languages: [python]
    severity: ERROR
  `,
    answers: [
      `rules:
  - id: use-decimalfield-for-money
    patterns:
    - pattern-inside: |
        class $M(...):
          ...
    - pattern: $F = django.db.models.FloatField(...)
    - metavariable-regex:
        metavariable: '$F'
        regex: '.*(price|fee|salary).*'
    message: Found a FloatField used for variable $F. Use DecimalField for currency fields to avoid float-rounding errors.
    languages: [python]
    severity: ERROR`,
      `rules:
  - id: use-decimalfield-for-money
    patterns:
    - pattern-inside: |
        class $M(...):
          ...
    - pattern: $F = django.db.models.FloatField(...)
    - metavariable-regex:
        metavariable: '$F'
        regex: '.*(fee|price|salary).*'
    message: Found a FloatField used for variable $F. Use DecimalField for currency fields to avoid float-rounding errors.
    languages: [python]
    severity: ERROR`,
      `rules:
  - id: use-decimalfield-for-money
    patterns:
    - pattern-inside: |
        class $M(...):
          ...
    - pattern: $F = django.db.models.FloatField(...)
    - metavariable-regex:
        metavariable: '$F'
        regex: '.*(fee|salary|price).*'
    message: Found a FloatField used for variable $F. Use DecimalField for currency fields to avoid float-rounding errors.
    languages: [python]
    severity: ERROR`,
    ],
    target: `from django.db import models

class Product(models.Model):
    name = models.CharField(max_length=64)
    description = models.TextField()
    # this is ok
    price = models.DecimalField(max_digits=6, decimal_places=2)
    # this is also ok
    return_rate = models.FloatField()
    # Semgrep finds this because old_fee ends in the word fee, which is in the regex above
    old_fee = models.FloatField()
    # match this
    price_inc = models.FloatField()
`,
    followupTitle: "Congratulations!",
    followup: (
      <>
        <p>You have completed all of our composition challenges!</p>
        <p>
          If you'd like to check out some of Semgrep's newest and shiniest
          features, continue onto the <Link to="/learn">advanced tutorial</Link>
          .
        </p>
        <span style={{ fontSize: "3em" }} role="img" aria-label=":train:">
          🚂
        </span>
      </>
    ),
  },
];
